import { createFileRoute } from "@tanstack/react-router";
import { buildSeo } from "@/lib/seo";
import { BlogArticle, articleHead, type Article } from "@/components/marketing/BlogArticle";

const article: Article = {
  path: "/blog/ai-twins-for-agencies",
  category: "Industry",
  readTime: "7 min read",
  published: "2026-07-24",
  title: "AI Twins for agencies:",
  titleEm: "a new service line.",
  lead: "Agencies are perfectly placed to win with AI Twins — twice. Deploy one for your own inbound, then productize it as a high-margin service for every client. Here's the playbook for both.",
  metaDescription:
    "How agencies turn AI Twins into a recurring service line: what to sell, how to price it, and how to run several client twins from one account.",
  takeaways: [
    "Run a Twin on your own site to qualify inbound and prove the value.",
    "Package 'AI Twin setup + management' as a recurring client service.",
    "It's high-margin, retainer-friendly, and differentiates your pitch.",
    "Multi-twin support means one account, many client twins.",
  ],
  blocks: [
    { type: "h2", text: "Why agencies are the ideal operator" },
    {
      type: "p",
      text: "An AI Twin is only as good as the knowledge and positioning behind it — which is exactly what agencies do for a living. You already gather a client's offer, audience, voice and FAQs. Turning that into a live, revenue-generating Twin is a natural extension of the work, not a new discipline.",
    },
    { type: "h2", text: "Move 1 — Deploy on your own site first" },
    {
      type: "p",
      text: "Before you sell it, run it. Put a Twin on your agency site so it qualifies inbound leads, answers scope and pricing questions, and books discovery calls while you sleep. You get more qualified pipeline and a live reference you can show every prospect: 'here's ours, working right now.'",
    },
    { type: "h2", text: "Move 2 — Productize it as a service" },
    {
      type: "p",
      text: "Package the offering so it's easy to buy and easy to deliver:",
    },
    {
      type: "ul",
      items: [
        <>
          <strong className="text-foreground">Setup fee:</strong> ingest the client's site and docs,
          tune voice, define qualification and handoff — a fixed onboarding project.
        </>,
        <>
          <strong className="text-foreground">Monthly retainer:</strong> monitor conversations,
          close knowledge gaps, tune, and report on leads generated.
        </>,
        <>
          <strong className="text-foreground">Performance upside:</strong> tie part of the fee to
          qualified leads or booked meetings the Twin creates.
        </>,
      ],
    },
    {
      type: "callout",
      title: "Why it's a great retainer",
      text: "A Twin is never 'done' — new pages, offers and questions appear constantly. That makes ongoing tuning a genuine, defensible monthly service, not a one-off build.",
    },
    { type: "h2", text: "The margin math" },
    {
      type: "p",
      text: "Software cost per client is a fraction of what you can charge for strategy, knowledge curation and management. Because the platform runs one shared conversation pool per account and supports many twins, you can operate a portfolio of client twins from a single seat and keep the spread.",
    },
    { type: "h2", text: "Positioning it to clients" },
    {
      type: "p",
      text: "Don't sell 'a chatbot'. Sell an always-on member of their team that qualifies leads and answers customers in their voice — and a public profile that gets them discovered in a network of company twins. It's a growth asset, priced like one.",
    },
    { type: "h2", text: "A 30-day launch plan for your agency" },
    {
      type: "ol",
      items: [
        "Week 1: deploy your own Twin; use it as the live demo.",
        "Week 2: package setup + retainer pricing and a one-page pitch.",
        "Week 3: pilot with two existing clients at a friendly rate.",
        "Week 4: turn the pilots into case studies and roll it into every proposal.",
      ],
    },
  ],
  faqs: [
    {
      q: "Can an agency resell AI Twins to clients?",
      a: "Yes. Agencies are ideal operators: you already do the knowledge, positioning and voice work a great Twin needs. Package it as a setup project plus a monthly management retainer, and run multiple client twins from one account.",
    },
    {
      q: "Is managing AI Twins a good recurring revenue stream?",
      a: "It's one of the better ones — a Twin needs ongoing tuning as a client's offers, pages and questions change, which makes a monthly retainer genuinely valuable rather than busywork.",
    },
    {
      q: "How many client twins can one agency run?",
      a: "As many as your plan allows — the platform supports multiple twins per account with one shared conversation pool, so agencies can operate a whole client portfolio from a single seat.",
    },
  ],
  related: [
    { slug: "what-is-an-ai-twin", title: "What is an AI Twin? The complete 2026 guide" },
    {
      slug: "convert-website-visitors-with-ai",
      title: "How to convert more website visitors with AI",
    },
    {
      slug: "best-ai-chatbot-alternatives-b2b",
      title: "The 7 best AI chatbot alternatives for B2B",
    },
  ],
};

export const Route = createFileRoute("/blog/ai-twins-for-agencies")({
  head: () =>
    articleHead(
      article,
      buildSeo({
        path: article.path,
        title: "AI Twins for Agencies: A New Service Line | Reality Twin",
        description: article.metaDescription ?? article.lead,
        type: "article",
      }),
    ),
  component: () => <BlogArticle article={article} />,
});
